import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Ref } from 'react';
import { layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import {
  exportActionTooltips,
  exportFieldTooltips,
} from '../../../shared/config/actionHelpCatalog';
import ColumnFilterMenu from '../../../shared/ui/table/ColumnFilterMenu';
import TableHeaderField from '../../../shared/ui/table/TableHeaderField';
import { getScopeEditorHeaderCellSx } from '../../../shared/ui/mapping/ScopeEditorPrimitives';
import { mappingDensity } from '../../../shared/ui/mapping';
import type { ExportColumn } from '../hooks/useExportColumns';
import type {
  ExportColumnKey,
  SystemTableModel,
} from '../hooks/useSystemTableModel';
import { getSystemTableSelectionCellSx } from './systemTableSelectionCellStyles';

const ExportHeaderActionSize = 26;

interface CommonHeaderProps {
  readonly model: SystemTableModel;
  readonly selectionDisabled: boolean;
}

function getColumnHeaderHelp(columnKey: ExportColumnKey) {
  if (columnKey === 'systemName') {
    return exportFieldTooltips.systemNameColumn;
  }
  if (columnKey === 'elementCount') {
    return exportFieldTooltips.elementCountColumn;
  }
  if (columnKey === 'sessionExportCount') {
    return exportFieldTooltips.sessionExportCountColumn;
  }
  if (columnKey === 'status') {
    return exportFieldTooltips.statusColumn;
  }
  return exportFieldTooltips.dynamicPropertyColumn;
}

function ColumnHeaderField({
  columnKey,
  label,
  model,
}: {
  readonly columnKey: ExportColumnKey;
  readonly label: string;
  readonly model: SystemTableModel;
}) {
  const activeFilter = model.columnFilters[columnKey];
  const filterValue = activeFilter?.mode === 'condition' ? activeFilter.value : '';
  const isSorted = model.sortBy === columnKey;

  return (
    <TableHeaderField
      label={label}
      value={filterValue}
      onChange={(value) => model.setColumnConditionFilter(columnKey, 'contains', value)}
      onSort={() => model.handleSort(columnKey)}
      sortDirection={isSorted ? model.sortDir : null}
      ariaLabel={columnKey === 'systemName' ? 'Search system name' : `Search ${label}`}
      help={getColumnHeaderHelp(columnKey)}
      sortHelp={exportActionTooltips.columnSort}
      tooltipPlacement="bottom-start"
      inputHeight={mappingDensity.tableInputCompactHeight}
      trailingAdornment={
        <ColumnFilterMenu
          columnLabel={label}
          filter={activeFilter}
          uniqueValues={model.getUniqueValuesForColumn(columnKey)}
          valueCounts={model.getValueCountsForColumn(columnKey)}
          onApplyValues={(values) => model.setColumnValueFilter(columnKey, values)}
          onApplyCondition={(operator, value) =>
            model.setColumnConditionFilter(columnKey, operator, value)}
          sortDirection={isSorted ? model.sortDir : null}
          onSortAscending={() => model.setSort(columnKey, 'asc')}
          onSortDescending={() => model.setSort(columnKey, 'desc')}
          onClearSort={model.clearSort}
          onClear={() => model.clearColumnFilter(columnKey)}
          help={exportActionTooltips.columnFilterMenu}
          triggerVariant="inline"
          triggerSize={ExportHeaderActionSize}
        />
      }
    />
  );
}

export function SystemTableMobileHeader({
  model,
  selectionDisabled,
  systemToolsRef,
}: CommonHeaderProps & { readonly systemToolsRef?: Ref<HTMLElement> }) {
  const theme = useTheme();

  return (
    <Box
      ref={systemToolsRef}
      data-testid="export-pcf-system-mobile-controls"
      data-tour-target="export-pcf-system-tools"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        alignItems: 'center',
        columnGap: layoutGap.dense,
        px: 1,
        py: 0.65,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.32)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.76),
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2, minWidth: 0 }}>
        <ActionHelpTooltip
          help={exportActionTooltips.selectVisibleSystems}
          placement="right"
          // Card checkboxes sit inset by the card border + px 0.85 + p 0.25;
          // mirror that inset so both rows share one vertical axis.
          wrapperSx={{ pl: '7px' }}
        >
          <Checkbox
            indeterminate={model.someSelected}
            checked={model.allSelected}
            onChange={model.handleSelectAll}
            disabled={selectionDisabled}
            size="small"
            inputProps={{
              'aria-label': model.allSelected
                ? 'Clear visible systems selection'
                : 'Select visible systems',
            }}
            sx={{ p: 0.35 }}
          />
        </ActionHelpTooltip>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.68rem',
            fontWeight: 700,
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
          }}
        >
          {model.selectedFilteredCount}/{model.filtered.length}
        </Typography>
      </Box>
      <TextField
        size="small"
        placeholder="Search systems"
        value={model.systemNameFilterValue}
        onChange={(event) =>
          model.setColumnConditionFilter('systemName', 'contains', event.target.value)}
        inputProps={{ 'aria-label': 'Search systems' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 0,
          '& .MuiOutlinedInput-root': {
            height: 30,
            fontSize: '0.74rem',
            backgroundColor: alpha(theme.palette.background.paper, 0.88),
          },
          '& .MuiOutlinedInput-input': { py: 0.45 },
        }}
      />
    </Box>
  );
}

export function SystemTableSelectionSummary({
  model,
  totalSystemCount,
}: {
  readonly model: SystemTableModel;
  readonly totalSystemCount: number;
}) {
  const theme = useTheme();
  if (!model.showSelectionStrip) {
    return null;
  }

  return (
    <Box
      data-testid="export-pcf-table-toolbar"
      data-tour-target="export-pcf-system-tools"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: layoutGap.row,
        px: 1.5,
        py: 0.25,
        '@media (max-height: 480px)': { py: 0.2 },
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.28)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.68),
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense, minWidth: 0 }}>
        {model.activeFilterCount > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={`${model.activeFilterCount} filter${model.activeFilterCount === 1 ? '' : 's'}`}
            onDelete={model.clearAllColumnFilters}
            deleteIcon={(
              <CancelIcon
                aria-label={`Clear ${model.activeFilterCount} active filter${model.activeFilterCount === 1 ? '' : 's'}`}
              />
            )}
            sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
          />
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: layoutGap.row, minWidth: 0 }}>
        {model.filtered.length !== totalSystemCount ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {model.filtered.length} of {totalSystemCount} systems
          </Typography>
        ) : null}
        {model.selectedFilteredCount > 0 ? (
          <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {model.selectedFilteredCount} selected
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export function SystemTableDesktopHeader({
  model,
  dynamicColumns,
  selectionDisabled,
}: CommonHeaderProps & { readonly dynamicColumns: ExportColumn[] }) {
  const theme = useTheme();
  const headerCellSx: SxProps<Theme> = {
    ...getScopeEditorHeaderCellSx(theme),
    verticalAlign: 'middle',
  };

  return (
    <TableHead>
      <TableRow ref={model.setToolsRegionRef} data-tour-target="export-pcf-system-tools">
        <TableCell
          sx={{
            ...headerCellSx,
            ...getSystemTableSelectionCellSx(model.fixedColumnWidth.selection),
            borderTopLeftRadius: 0,
          }}
        >
          <ActionHelpTooltip
            help={exportActionTooltips.selectVisibleSystems}
            placement="right"
            fullWidth
            wrapperSx={{ justifyContent: 'center' }}
          >
            <Checkbox
              indeterminate={model.someSelected}
              checked={model.allSelected}
              onChange={model.handleSelectAll}
              disabled={selectionDisabled}
              size="small"
              inputProps={{
                'aria-label': model.allSelected
                  ? 'Clear visible systems selection'
                  : 'Select visible systems',
              }}
              // Unwrapped row checkboxes get padding 0 from the table cell
              // rule; the tooltip wrapper shields this one, so reset it here
              // to keep the column on a single vertical axis.
              sx={{ p: 0 }}
            />
          </ActionHelpTooltip>
        </TableCell>
        <TableCell aria-sort={model.getSortAria('systemName')} sx={{ ...headerCellSx, width: model.fixedColumnWidth.systemName }}>
          <ColumnHeaderField columnKey="systemName" label="System name" model={model} />
        </TableCell>
        <TableCell aria-sort={model.getSortAria('elementCount')} sx={{ ...headerCellSx, width: model.fixedColumnWidth.elementCount, textAlign: 'right' }}>
          <ColumnHeaderField columnKey="elementCount" label="Elements" model={model} />
        </TableCell>
        <TableCell aria-sort={model.getSortAria('sessionExportCount')} sx={{ ...headerCellSx, width: model.fixedColumnWidth.sessionExportCount, textAlign: 'right' }}>
          <ColumnHeaderField columnKey="sessionExportCount" label="Exports" model={model} />
        </TableCell>
        {dynamicColumns.map((column) => (
          <TableCell key={column.id} aria-sort={model.getSortAria(column.id)} sx={headerCellSx}>
            <ColumnHeaderField columnKey={column.id} label={column.label} model={model} />
          </TableCell>
        ))}
        <TableCell aria-sort={model.getSortAria('status')} sx={{ ...headerCellSx, width: model.fixedColumnWidth.status, textAlign: 'center' }}>
          <ColumnHeaderField columnKey="status" label="Status" model={model} />
        </TableCell>
        <TableCell sx={{ ...headerCellSx, width: model.fixedColumnWidth.model, borderTopRightRadius: 0, textAlign: 'center' }}>
          <ActionHelpTooltip help={exportActionTooltips.selectSystemInModel} placement="bottom-start">
            <Typography component="span" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit', lineHeight: 1.15, textTransform: 'inherit', whiteSpace: 'nowrap' }}>
              Model
            </Typography>
          </ActionHelpTooltip>
        </TableCell>
      </TableRow>
    </TableHead>
  );
}
